import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { filter, tap } from 'rxjs/operators';
import { DashboardToolOption } from '../project-dashboard-header/project-dashboard-header.component';
import { ToolsOptions } from '../project-dashboard-header/project-dashboard-header.const';
import {
  DASHBOARD_OVERVIEW_KEY,
  getDashboardPathForTool,
  getDashboardToolKeyFromUrl
} from '../../project-dashboard-navigation.const';
import { TranslateModule } from '@ngx-translate/core';

interface DockItem extends DashboardToolOption {
  isNew?: boolean;
}

interface DockRenderItem extends DockItem {
  isPlaceholder?: boolean;
}

interface ActiveDrag {
  item: DockItem;
  source: 'tools' | 'dock';
  sourceIndex?: number;
}

const DOCK_STORAGE_KEY = 'project-dashboard-dock-items-v1';
const OVERVIEW_KEY = DASHBOARD_OVERVIEW_KEY;
const OVERVIEW_ITEM: DockItem = {
  key: OVERVIEW_KEY,
  label: 'Übersicht',
  icon: '/assets/img/ic-summary-dock.svg'
};

@Component({
  selector: 'project-dashboard-dock',
  standalone: true,
  templateUrl: './project-dashboard-dock.component.html',
  styleUrls: ['./project-dashboard-dock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AngularSvgIconModule, TranslateModule]
})
export class ProjectDashboardDockComponent implements OnInit, OnChanges, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  allToolOptions = ToolsOptions;

  draggedToolFromHeader = input<DashboardToolOption | null>(null);
  currentRoute = input<string | null>(null);

  readonly dockItems = signal<DockItem[]>([OVERVIEW_ITEM]);
  readonly activeDrag = signal<ActiveDrag | null>(null);
  readonly isDockDragOver = signal(false);
  readonly placeholderIndex = signal<number | null>(null);
  readonly dragCursor = signal<{ x: number; y: number }>({ x: 0, y: 0 });
  readonly trashVisibleItemKey = signal<string | null>(null);
  readonly currentUrl = signal('');

  private trashHoverTimer: number | null = null;
  private dragImageAnchor: HTMLElement | null = null;
  private preventClickUntil = 0;

  @HostListener('document:pointerup')
  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  protected onAnyPointerRelease(): void {
    if (this.activeDrag()) this.resetDockDragState();
  }

  @HostListener('document:drop')
  protected onDocumentDrop(): void {
    if (this.activeDrag()) this.resetDockDragState();
  }

  @HostListener('document:dragend')
  protected onDocumentDragEnd(): void {
    if (this.activeDrag()) this.resetDockDragState();
  }

  @HostListener('window:blur')
  protected onWindowBlur(): void {
    if (this.activeDrag()) this.resetDockDragState();
  }

  @HostListener('document:dragover', ['$event'])
  protected onDocumentDragOver(event: DragEvent): void {
    if (!this.activeDrag()) return;
    // Enables immediate global drop delivery even outside dock area.
    event.preventDefault();
    this.dragCursor.set({ x: event.clientX, y: event.clientY });
  }

  readonly dockRenderItems = computed<DockRenderItem[]>(() => {
    const drag = this.activeDrag();
    const rawItems = this.dockItems();
    const items: DockRenderItem[] =
      drag?.source === 'dock' && drag.sourceIndex !== undefined
        ? rawItems.filter((_, index) => index !== drag.sourceIndex)
        : [...rawItems];

    const placeholderAt = this.placeholderIndex();
    const shouldShowPlaceholder =
      placeholderAt !== null && (drag?.source === 'dock' || this.isDockDragOver());
    if (!shouldShowPlaceholder) return items;

    items.splice(placeholderAt, 0, { key: '__placeholder__', label: '', isPlaceholder: true });
    return items;
  });

  protected readonly activeDockKey = computed<string>(() => {
    return getDashboardToolKeyFromUrl(this.currentUrl());
  });

  protected readonly isBottomBarVisible = computed<boolean>(
    () => this.currentRoute() === 'changes' || this.currentRoute() === 'configurator'
  );

  ngOnInit(): void {
    this.initRouterEvents();
    this.loadDockItemsFromStorage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['draggedToolFromHeader']) return;

    if (this.draggedToolFromHeader()) {
      this.activeDrag.set({
        source: 'tools',
        item: {
          key: this.draggedToolFromHeader().key,
          label: this.draggedToolFromHeader().label,
          icon: this.draggedToolFromHeader().icon
        }
      });
      return;
    }

    if (this.activeDrag()?.source === 'tools') this.resetDockDragState();
  }

  private initRouterEvents(): void {
    this.currentUrl.set(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        tap(({ url, urlAfterRedirects }) => this.currentUrl.set(urlAfterRedirects || url)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected onDockItemDragStart(event: DragEvent, item: DockItem, index: number): void {
    if (!event.dataTransfer) return;

    event.stopPropagation();
    event.dataTransfer.setData('text/plain', item.key);
    event.dataTransfer.setData('application/x-intilio-dock', item.key);
    event.dataTransfer.effectAllowed = 'move';
    this.dragImageAnchor = this.createHiddenDragImageAnchor();
    event.dataTransfer.setDragImage(this.dragImageAnchor, 0, 0);

    this.activeDrag.set({ item, source: 'dock', sourceIndex: index });
    this.isDockDragOver.set(true);
    this.placeholderIndex.set(index);
    this.dragCursor.set({ x: event.clientX, y: event.clientY });
    this.trashVisibleItemKey.set(null);
    this.clearTrashHoverTimer();
  }

  protected onDockItemDragEnd(): void {
    this.preventClickUntil = Date.now() + 200;
    this.resetDockDragState();
  }

  protected onDockItemClick(item: DockItem): void {
    if (Date.now() < this.preventClickUntil) return;
    const routePath = getDashboardPathForTool(item.key);
    const projectId = this.resolveProjectIdFromRoute();
    if (!projectId) return;

    this.router.navigate(['/panel/projects/dashboard', projectId, routePath], {
      queryParams: { tool: item.key }
    });
  }

  protected isDockItemActive(itemKey: string): boolean {
    return this.activeDockKey() === itemKey;
  }

  protected onDockItemMouseEnter(itemKey: string): void {
    if (this.activeDrag()) return;
    if (itemKey === OVERVIEW_KEY) return;

    this.clearTrashHoverTimer();
    this.trashHoverTimer = window.setTimeout(() => {
      if (this.dockItems().length > 1 && itemKey !== OVERVIEW_KEY) this.trashVisibleItemKey.set(itemKey);
    }, 1000);
  }

  protected onDockItemMouseLeave(itemKey: string): void {
    this.clearTrashHoverTimer();
    if (this.trashVisibleItemKey() === itemKey) this.trashVisibleItemKey.set(null);
  }

  protected onDockItemRemove(event: MouseEvent, itemKey: string): void {
    event.preventDefault();
    event.stopPropagation();

    if (itemKey === OVERVIEW_KEY) return;
    if (this.dockItems().length <= 1) return;

    const next = this.dockItems().filter((item) => item.key !== itemKey);
    this.setDockItemsAndPersist(next);
    this.trashVisibleItemKey.set(null);
    this.clearTrashHoverTimer();
  }

  protected onDockDragOver(event: DragEvent): void {
    const drag = this.activeDrag();
    if (!drag) return;

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = drag.source === 'dock' ? 'move' : 'copy';

    this.isDockDragOver.set(true);
    this.placeholderIndex.set(this.calculatePlaceholderIndex(event));
    this.dragCursor.set({ x: event.clientX, y: event.clientY });
  }

  protected onDockDragLeave(event: DragEvent): void {
    const drag = this.activeDrag();
    const dock = event.currentTarget as HTMLElement;
    const rect = dock.getBoundingClientRect();
    const isStillInside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (isStillInside) return;

    this.isDockDragOver.set(false);
    if (drag?.source !== 'dock') this.placeholderIndex.set(null);
  }

  protected onDockDrop(event: DragEvent): void {
    event.preventDefault();

    const drag = this.activeDrag();
    if (!drag) return;

    const insertAt = this.placeholderIndex() ?? this.dockRenderItems().length;
    const current = this.dockItems();

    if (drag.source === 'tools') {
      if (current.some((item) => item.key === drag.item.key)) {
        this.resetDockDragState();
        return;
      }

      const next = [...current];
      next.splice(insertAt, 0, { ...drag.item, isNew: true });
      this.setDockItemsAndPersist(next);

      window.setTimeout(() => {
        this.updateDockItemsAndPersist((items) => items.map((item) => ({ ...item, isNew: false })));
      }, 320);
    }

    if (drag.source === 'dock') {
      const withoutDragged = current.filter((_, index) => index !== drag.sourceIndex);
      withoutDragged.splice(insertAt, 0, { ...drag.item, isNew: false });
      this.setDockItemsAndPersist(withoutDragged);
    }

    this.resetDockDragState();
  }

  private calculatePlaceholderIndex(event: DragEvent): number {
    const dock = event.currentTarget as HTMLElement;
    const dockItems = Array.from(dock.querySelectorAll<HTMLElement>('[data-dock-item="real"]'));

    for (let i = 0; i < dockItems.length; i++) {
      const rect = dockItems[i].getBoundingClientRect();
      if (event.clientX < rect.left + rect.width / 2) {
        return i;
      }
    }

    return dockItems.length;
  }

  private resetDockDragState(): void {
    this.activeDrag.set(null);
    this.isDockDragOver.set(false);
    this.placeholderIndex.set(null);
    this.trashVisibleItemKey.set(null);
    this.clearTrashHoverTimer();
    this.cleanupDragImageAnchor();
  }

  private createHiddenDragImageAnchor(): HTMLElement {
    const anchor = document.createElement('div');
    anchor.style.position = 'fixed';
    anchor.style.top = '-9999px';
    anchor.style.left = '-9999px';
    anchor.style.width = '1px';
    anchor.style.height = '1px';
    anchor.style.opacity = '0';
    anchor.style.pointerEvents = 'none';
    document.body.appendChild(anchor);
    return anchor;
  }

  private cleanupDragImageAnchor(): void {
    this.dragImageAnchor?.remove();
    this.dragImageAnchor = null;
  }

  private setDockItemsAndPersist(items: DockItem[]): void {
    const normalized = this.normalizeDockItems(items);
    this.dockItems.set(normalized);
    this.persistDockItems(normalized);
  }

  private updateDockItemsAndPersist(updater: (items: DockItem[]) => DockItem[]): void {
    const next = updater(this.dockItems());
    this.dockItems.set(next);
    this.persistDockItems(next);
  }

  private persistDockItems(items: DockItem[]): void {
    const payload = this.normalizeDockItems(items).map((item) => item.key);
    localStorage.setItem(DOCK_STORAGE_KEY, JSON.stringify(payload));
  }

  private loadDockItemsFromStorage(): void {
    const storageRaw = localStorage.getItem(DOCK_STORAGE_KEY);
    if (!storageRaw) {
      this.persistDockItems(this.dockItems());
      return;
    }

    try {
      const parsed = JSON.parse(storageRaw) as string[];
      if (!Array.isArray(parsed) || parsed.length === 0) return;

      const entries: Array<[string, DockItem]> = [
        [OVERVIEW_KEY, OVERVIEW_ITEM],
        ...this.allToolOptions.map((option) => [option.key, { ...option } as DockItem] as [string, DockItem])
      ];
      const catalog = new Map<string, DockItem>(entries);

      const restored = parsed
        .map((key) => catalog.get(key))
        .filter((item): item is DockItem => Boolean(item));

      if (restored.length > 0) {
        const normalized = this.normalizeDockItems(restored);
        this.dockItems.set(normalized);
        this.persistDockItems(normalized);
      }
    } catch {
      this.persistDockItems(this.dockItems());
    }
  }

  private normalizeDockItems(items: DockItem[]): DockItem[] {
    const seen = new Set<string>();
    const unique: DockItem[] = [];

    for (const item of items) {
      if (!item?.key || seen.has(item.key)) continue;
      seen.add(item.key);
      unique.push(item);
    }

    if (!seen.has(OVERVIEW_KEY)) {
      unique.unshift(OVERVIEW_ITEM);
      return unique;
    }

    const overview = unique.find((item) => item.key === OVERVIEW_KEY) ?? OVERVIEW_ITEM;
    const rest = unique.filter((item) => item.key !== OVERVIEW_KEY);
    return [overview, ...rest];
  }

  protected isRemovableDockItem(itemKey: string): boolean {
    return itemKey !== OVERVIEW_KEY && this.dockItems().length > 1;
  }

  private clearTrashHoverTimer(): void {
    if (this.trashHoverTimer !== null) {
      window.clearTimeout(this.trashHoverTimer);
      this.trashHoverTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearTrashHoverTimer();
    this.cleanupDragImageAnchor();
  }

  private resolveProjectIdFromRoute(): number | null {
    const routeChain = this.activatedRoute.pathFromRoot.slice().reverse();

    for (const route of routeChain) {
      const projectIdParam = route.snapshot.paramMap.get('projectId');
      if (projectIdParam) {
        const parsed = Number(projectIdParam);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
      }

      const idParam = route.snapshot.paramMap.get('id');
      if (idParam) {
        const parsed = Number(idParam);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
      }
    }

    return null;
  }
}
